/**
 * Per-tab analytics session identity.
 *
 * A UUID in `sessionStorage`, which means: new id per tab, gone when the tab
 * closes, never shared between tabs. That is deliberately weaker than a
 * localStorage device id — it is not a persistent identifier, so it stays clear
 * of the consent obligations one would attract, and per-tab granularity is
 * enough for the questions this data answers.
 *
 * What it buys: a guest browsing `/browse` and then signing in produces events
 * that share a `session_id`, so the signup funnel can be stitched together even
 * though the early events have no `user_id`.
 *
 * If you ever want cross-visit funnels, that is a different identifier with a
 * different privacy story. Do not quietly promote this one to localStorage.
 *
 * ## A device profile is attached to this id
 *
 * `session_start` records browser, OS, device type, timezone, locale and screen
 * against the id below — see `device.ts`. Taken together those fields are a
 * browser-fingerprinting surface, so the per-tab, non-persistent nature of this
 * identifier is what keeps it from becoming one: the profile cannot be joined
 * across visits because the id it hangs off does not survive them. Promoting this
 * to localStorage would turn a session description into cross-visit tracking of an
 * individual. Related: the server already stores the raw user-agent and IP per
 * event, and `Analytics.anonymize_for_user/1` nulls all of it on GDPR erasure.
 */

const SESSION_KEY = "rb_analytics_session";

// Safari in private mode, and any browser with storage disabled, throws on
// sessionStorage access rather than returning null. Falling back to a
// process-lifetime id keeps events attributable within the page load instead of
// dropping them.
let fallbackId: string | null = null;

/**
 * Called with the new id whenever one is minted, so `session_start` can be
 * emitted for it.
 *
 * A registered listener rather than importing `track()` directly, because
 * `session → index → session` is an import cycle — and cycles in this app do not
 * fail loudly, they leave a binding `undefined` at module-eval time. Injection
 * also keeps this file dependency-free while leaving it as the single place that
 * knows a session began, which is what makes the awkward second mint path — a
 * fresh id after `clearSessionId()` on logout — covered without a second call
 * site remembering to.
 */
let onMint: ((id: string) => void) | null = null;

/** Ids already announced, so a re-registered listener cannot double-report one. */
const announced = new Set<string>();

export const onSessionMint = (listener: (id: string) => void): void => {
  onMint = listener;
};

/** Test seam: forget the listener and everything it has announced. */
export const resetSessionMintListener = (): void => {
  onMint = null;
  announced.clear();
};

const announceMint = (id: string): void => {
  if (!onMint || announced.has(id)) return;
  announced.add(id);

  try {
    onMint(id);
  } catch (error) {
    // Session identity must survive a broken analytics event. This runs deep
    // inside buildPageViewEvent, so throwing here would cost the page view too.
    console.warn("[analytics] session_start failed to build", error);
  }
};

const newId = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // jsdom and older browsers. Not cryptographically meaningful — this is a
  // correlation key, not a secret.
  return `s-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
};

export const getSessionId = (): string => {
  try {
    const existing = window.sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;

    const id = newId();
    window.sessionStorage.setItem(SESSION_KEY, id);
    announceMint(id);
    return id;
  } catch {
    // Both mint paths announce. The private-mode fallback is a real session with a
    // real device behind it, and it is exactly the population most likely to be
    // under-reported already.
    if (!fallbackId) {
      fallbackId = newId();
      announceMint(fallbackId);
    }
    return fallbackId;
  }
};

/**
 * Drops the session identity. Call on logout so the next person at the same
 * browser does not inherit the previous one's session.
 *
 * The next `getSessionId()` mints a fresh id and therefore announces a fresh
 * `session_start`. That is the intent: the person now at the keyboard may be a
 * different one, on the same device, and the new session says so.
 */
export const clearSessionId = (): void => {
  try {
    window.sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // Nothing to clear if storage was never reachable.
  }
  fallbackId = null;
};
